import React from "react";
import {
  Card,
  CardHeader,
  CardContent,
  Box,
  Typography,
  Chip,
  IconButton,
} from "@material-ui/core";
import LaunchIcon from "@material-ui/icons/Launch";
import { useHistory } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { getInvoicesByUser } from "../../actions/invoiceActions";

const currencyFmt = (v, code = "USD") =>
  new Intl.NumberFormat(undefined, {
    style: "currency",
    currency: code,
    maximumFractionDigits: 0,
  }).format(v || 0);

const formatDate = (value) => {
  if (!value) return "No due date";
  const date = value instanceof Date ? value : new Date(value);
  if (Number.isNaN(+date)) return "No due date";
  return date.toLocaleDateString(undefined, {
    month: "short",
    day: "numeric",
    year: "numeric",
  });
};

const computeOutstanding = (invoice = {}) => {
  const total = Number(invoice.total ?? 0);
  const explicitReceived = Number(invoice.totalAmountReceived ?? 0);
  if (explicitReceived) return Math.max(total - explicitReceived, 0);
  const payments = Array.isArray(invoice.paymentRecords)
    ? invoice.paymentRecords.reduce(
        (sum, rec) => sum + Number(rec?.amountPaid || 0),
        0
      )
    : 0;
  return Math.max(total - payments, 0);
};

export default function OverdueInvoicesCard({
  title = "Overdue Invoices",
  currency = "USD",
  maxItems = 6,
  autoLoad = true,
  invoices: preloadedInvoices,
  loading,
}) {
  const history = useHistory();
  const dispatch = useDispatch();
  const { invoices = [], isLoading: storeIsLoading = false } = useSelector(
    (state) => state?.invoices || {}
  );
  const sourceInvoices = Array.isArray(preloadedInvoices) ? preloadedInvoices : invoices;
  const isLoading = typeof loading === "boolean" ? loading : storeIsLoading;

  const userInfo = React.useMemo(() => {
    if (typeof window === "undefined") return null;
    try {
      const stored = JSON.parse(localStorage.getItem("profile") || "null");
      return stored?.result || stored;
    } catch {
      return null;
    }
  }, []);

  React.useEffect(() => {
    if (Array.isArray(preloadedInvoices)) return;
    if (!autoLoad) return;
    const id = userInfo?._id || userInfo?.googleId;
    if (!id) return;
    if (invoices?.length) return;
    dispatch(getInvoicesByUser({ search: id }));
  }, [autoLoad, dispatch, invoices?.length, preloadedInvoices, userInfo]);

  const overdueInvoices = React.useMemo(() => {
    if (Array.isArray(preloadedInvoices)) {
      return sourceInvoices.slice(0, maxItems);
    }
    const now = new Date();
    return (sourceInvoices || [])
      .filter((inv) => {
        if (!inv?.dueDate) return false;
        const due = new Date(inv.dueDate);
        const isPastDue = due < now;
        const status = (inv?.status || "").toLowerCase();
        const isOutstanding =
          status === "unpaid" || status === "partial" || status === "pending";
        return isPastDue && isOutstanding && computeOutstanding(inv) > 0;
      })
      .sort(
        (a, b) => new Date(a.dueDate || 0) - new Date(b.dueDate || 0)
      )
      .slice(0, maxItems)
      .map((inv) => ({
        ...inv,
        outstanding: computeOutstanding(inv),
      }));
  }, [maxItems, preloadedInvoices, sourceInvoices]);

  return (
    <Card variant="outlined" style={{ height: "100%" }}>
      <CardHeader
        title={title}
        subheader={
          overdueInvoices.length
            ? `${overdueInvoices.length} invoice${
                overdueInvoices.length === 1 ? "" : "s"
              }`
            : undefined
        }
      />
      <CardContent style={{ maxHeight: 320, overflowY: "auto" }}>
        {isLoading ? (
          <Typography variant="body2" color="textSecondary">
            Loading invoices...
          </Typography>
        ) : overdueInvoices.length === 0 ? (
          <Typography variant="body2" color="textSecondary">
            No overdue invoices
          </Typography>
        ) : (
          overdueInvoices.map((inv) => (
            <Box
              key={inv._id}
              display="flex"
              justifyContent="space-between"
              alignItems="center"
              py={0.75}
              borderBottom="1px solid rgba(0,0,0,0.06)"
              style={{ cursor: "pointer" }}
              onClick={() => history.push(`/invoice/${inv._id}`)}
            >
              <Box>
                <Typography variant="body2" style={{ fontWeight: 600 }}>
                  {inv?.client?.store ||
                    inv?.client?.name ||
                    inv.invoiceNumber ||
                    "Invoice"}
                </Typography>
                <Typography variant="caption" color="textSecondary">
                  Due {formatDate(inv.dueDate)}
                </Typography>
              </Box>
              <Box textAlign="right">
                <Typography variant="body2" style={{ fontWeight: 600 }}>
                  {currencyFmt(inv.outstanding, currency)}
                </Typography>
                <Chip
                  size="small"
                  label={inv.status || "Unpaid"}
                  style={inv.status === "Unpaid" ? { marginTop: 4, backgroundColor: "#b91c1c", color: "#fff" } : {marginTop: 4, backgroundColor: "rgba(49, 126, 204, 1)", color: "#fff"}}
                />
                <Box
                  display="flex"
                  alignItems="center"
                  justifyContent="flex-end"
                  mt={0.5}
                  style={{ gap: 4 }}
                >
                  <Typography variant="caption" color="primary">
                    View Invoice
                  </Typography>
                  <IconButton size="small" style={{ padding: 0 }}>
                    <LaunchIcon fontSize="small" color="primary" />
                  </IconButton>
                </Box>
              </Box>
            </Box>
          ))
        )}
      </CardContent>
    </Card>
  );
}
